import { LoanFormat, parseLoanFormat } from '../../../models/LoanFormat';
import { normalizeAllLanguagesUrlKey } from '../../shop/language/constants';
import { BucketEntry, LanguageSidebarAggregationResponse } from '../../store/api';
import { AgeCategory } from '../../../models/AgeCategory';
import { shopPrefix } from '../../config';
import { mapValidItems } from '../mapping';
import { isNotEmpty, isPositive, isPresent, validateAll, validateAndLogOnErrors, validateField } from '../validation';
import { http } from '../axios';

const ENDPOINT_LANGUAGE_LEARNING_LANGUAGES = `${shopPrefix}/v1/entrypages/languagelearning`;
const ENDPOINT_LOTE_LANGUAGES = `${shopPrefix}/v1/entrypages/lote`;

type LanguageBucketEntry = {
    readonly key: string;
    readonly docCount: number;
};

export type FetchLanguagesResponse = {
    readonly buckets: ReadonlyArray<LanguageBucketEntry>;
};

export type FetchLanguagesParams = {
    readonly loanFormat?: LoanFormat;
    readonly ageCategory?: AgeCategory;
};

export function fetchLanguageLearningLanguages(params: FetchLanguagesParams): Promise<FetchLanguagesResponse> {
    return fetchLanguages(params, ENDPOINT_LANGUAGE_LEARNING_LANGUAGES);
}

export function fetchLoteLanguages(params: FetchLanguagesParams): Promise<FetchLanguagesResponse> {
    return fetchLanguages(params, ENDPOINT_LOTE_LANGUAGES);
}

function fetchLanguages(params: FetchLanguagesParams, endpoint: string): Promise<FetchLanguagesResponse> {
    const parsedLoanFormat = parseLoanFormat(params.loanFormat);

    return http
        .get<LanguageSidebarAggregationResponse>(endpoint, {
            params: {
                ageCategory: params.ageCategory,
                loanFormat: parsedLoanFormat ? parsedLoanFormat.toUpperCase() : undefined
            }
        })
        .then(res => res.data)
        .then(data => ({
            buckets: mapValidItems(data.buckets ?? [], isValidBucketEntry, mapToLanguageBucketEntry)
        }));
}

const validateKey = validateAll(isPresent, isNotEmpty);
const validateDocCount = validateAll(isPresent, isPositive);

const languageBucketEntryValidation = validateAll<BucketEntry>(
    validateField('key', validateKey),
    validateField('docCount', validateDocCount)
);

function isValidBucketEntry(bucketEntry: BucketEntry): boolean {
    return validateAndLogOnErrors(languageBucketEntryValidation, bucketEntry);
}

function mapToLanguageBucketEntry(bucketEntry: BucketEntry): LanguageBucketEntry {
    return {
        key: normalizeAllLanguagesUrlKey(bucketEntry.key)!,
        docCount: bucketEntry.docCount!
    };
}
